import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, FileSpreadsheet, History, Loader2 } from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/api/client';
import TrackingBadge from '@/components/mail/TrackingBadge';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const when = (value) => formatInTimeZone(new Date(value), 'America/New_York', 'MMM d, yyyy');

const REPORT_TYPES = [
  { value: 'shipment', label: 'Shipments', hint: 'Every label bought, with its cost.' },
  { value: 'payment_log', label: 'Payment log', hint: 'Each charge and refund against the EasyPost wallet.' },
  { value: 'refund', label: 'Refunds', hint: 'Refund requests and how USPS settled them.' },
  { value: 'cash_flow', label: 'Cash flow', hint: 'Money in and out of the wallet.' },
  { value: 'insurance', label: 'Insurance', hint: 'Insured shipments and their fees.' },
  { value: 'tracker', label: 'Trackers', hint: 'Tracking status of each parcel.' },
  { value: 'shipment_invoice', label: 'Shipment invoice', hint: 'Line items as invoiced.' },
];

/**
 * What EasyPost itself holds for the account.
 *
 * The rest of ShipSwift reports on what this app did. This answers a different
 * question - does EasyPost agree? - and covers things the app never sees:
 * labels made in EasyPost's own dashboard, wallet payments, how USPS actually
 * settled a refund. For checking a month against the statement.
 *
 * Administrator-only: it shows the whole account, not just this app's labels.
 */
export default function EasyPostPage() {
  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6 min-w-0 w-full">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">EasyPost account</h1>
        <p className="text-slate-600">Everything EasyPost has on record, and EasyPost&apos;s own reports</p>
      </div>

      <Tabs defaultValue="shipments">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="shipments" className="gap-2">
            <History className="w-4 h-4" />
            Shipments
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="shipments" className="mt-6">
          <AccountShipments />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <AccountReports />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountShipments() {
  const queryClient = useQueryClient();
  const [pages, setPages] = useState([]); // cursors already loaded, for "load more"
  const [selected, setSelected] = useState(() => new Set());

  const [cursor, setCursor] = useState(undefined);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['easypostShipments', cursor ?? 'first'],
    queryFn: () =>
      api.request(`/easypost/shipments${cursor ? `?beforeId=${encodeURIComponent(cursor)}` : ''}`),
    retry: false,
  });

  // Pages accumulate as "load more" is pressed; the current page is derived
  // from the query so a refetch after an import shows the new "known" flags.
  const shown = [...pages.flatMap((page) => page.shipments), ...(data?.shipments ?? [])];
  const importable = shown.filter((s) => !s.known && s.trackingCode);

  const importMutation = useMutation({
    mutationFn: () =>
      api.request('/easypost/shipments/import', { method: 'POST', body: { shipmentIds: [...selected] } }),
    onSuccess: (result) => {
      setSelected(new Set());
      // Back to the first page: earlier pages are held in state, and their "in the
      // Shipped list" flags would otherwise be out of date.
      setPages([]);
      setCursor(undefined);
      queryClient.invalidateQueries({ queryKey: ['easypostShipments'] });
      queryClient.invalidateQueries({ queryKey: ['mailLabels'] });

      if (result.failed.length > 0) {
        toast.warning(`Imported ${result.imported}. ${result.failed.length} could not be imported: ${result.failed[0].reason}`);
      } else {
        toast.success(`Imported ${result.imported} label${result.imported === 1 ? '' : 's'}`);
      }
    },
    onError: (err) => toast.error(err.message ?? 'Could not import those shipments'),
  });

  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (isError) {
    return (
      <Alert className="border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          {error.message ?? 'Could not load shipments from EasyPost.'} Check the EasyPost key in Settings.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3">
          <span>Bought through this EasyPost account</span>
          <Button
            size="sm"
            disabled={selected.size === 0 || importMutation.isPending}
            onClick={() => importMutation.mutate()}
          >
            {importMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Import {selected.size > 0 ? `${selected.size} ` : ''}to the Shipped list
          </Button>
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isLoading && shown.length === 0 ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : shown.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">Nothing has been bought through this account yet.</p>
        ) : (
          <>
            <div className="px-4 py-3 border-b flex items-center gap-2 text-sm">
              <Checkbox
                checked={importable.length > 0 && selected.size === importable.length}
                onCheckedChange={(checked) =>
                  setSelected(checked === true ? new Set(importable.map((s) => s.id)) : new Set())
                }
                disabled={importable.length === 0}
              />
              <span className="text-slate-600">
                {importable.length === 0
                  ? 'Everything shown is already in the Shipped list'
                  : `${importable.length} not in the Shipped list yet`}
              </span>
            </div>

            <ul className="divide-y">
              {shown.map((shipment) => (
                <li key={shipment.id} className="flex items-start gap-3 px-4 py-3">
                  <Checkbox
                    className="mt-1"
                    disabled={shipment.known}
                    checked={selected.has(shipment.id)}
                    onCheckedChange={() => toggle(shipment.id)}
                  />
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="font-medium text-slate-900">
                      {shipment.recipient ?? 'Unknown recipient'}
                      <span className="font-normal text-slate-500">
                        {shipment.destination ? ` — ${shipment.destination}` : ''}
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {[shipment.carrier, shipment.service].filter(Boolean).join(' — ')}
                      {shipment.createdAt ? ` · ${when(shipment.createdAt)}` : ''}
                    </p>
                    <p className="text-xs font-mono text-slate-500">{shipment.trackingCode}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="font-semibold text-slate-900">{currency.format(shipment.cost)}</span>
                    <TrackingBadge status={shipment.trackingStatus} />
                    {shipment.refundStatus && (
                      <Badge variant="secondary" className="font-normal">
                        Refund {shipment.refundStatus}
                      </Badge>
                    )}
                    {shipment.known && (
                      <Badge variant="outline" className="font-normal">
                        In Shipped list
                      </Badge>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {data?.hasMore && (
              <div className="p-4 border-t text-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPages((current) => [...current, { shipments: data.shipments }]);
                    setCursor(data.nextBeforeId);
                  }}
                >
                  Load older
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function downloadCsv(filename, csv) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function AccountReports() {
  const [type, setType] = useState('shipment');
  const [range, setRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  });
  const [result, setResult] = useState(null);

  const run = useMutation({
    mutationFn: () =>
      api.request('/easypost/reports', {
        method: 'POST',
        body: { type, startDate: range.from, endDate: range.to },
      }),
    onSuccess: setResult,
    onError: (error) => toast.error(error.message ?? 'Could not run that report'),
  });

  const check = useMutation({
    mutationFn: () => api.request(`/easypost/reports/${result.type}/${result.reportId}`),
    onSuccess: setResult,
    onError: (error) => toast.error(error.message ?? 'Could not check on that report'),
  });

  const chosen = REPORT_TYPES.find((t) => t.value === type);
  const lines = result?.csv ? result.csv.split(/\r?\n/).filter(Boolean) : [];

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-slate-100 to-slate-50">
        <CardTitle>EasyPost&apos;s own reports</CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-5 min-w-0">
        <p className="text-sm text-slate-600">
          For checking a month against EasyPost&apos;s statement. Each report covers up to 30 days and
          downloads as a spreadsheet.
        </p>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1 w-56">
            <Label>Report</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="report-from" className="text-xs">From</Label>
            <Input
              id="report-from"
              type="date"
              value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="report-to" className="text-xs">To</Label>
            <Input
              id="report-to"
              type="date"
              value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
              className="h-9"
            />
          </div>

          <Button disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Preparing…
              </>
            ) : (
              'Get report'
            )}
          </Button>
        </div>

        {chosen && <p className="text-xs text-slate-500">{chosen.hint}</p>}

        {result?.status === 'empty' && (
          <Alert>
            <AlertDescription>EasyPost has nothing to report for those dates.</AlertDescription>
          </Alert>
        )}

        {result?.status === 'failed' && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">EasyPost could not produce that report. Try again.</AlertDescription>
          </Alert>
        )}

        {result?.status === 'pending' && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-800 flex flex-wrap items-center gap-3">
              EasyPost is still preparing the report.
              <Button size="sm" variant="outline" disabled={check.isPending} onClick={() => check.mutate()}>
                Check again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {result?.status === 'available' && (
          <div className="space-y-3 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-slate-700">
                {Math.max(lines.length - 1, 0)} row{lines.length - 1 === 1 ? '' : 's'} · {result.filename}
              </p>
              <Button size="sm" className="gap-2" onClick={() => downloadCsv(result.filename, result.csv)}>
                <Download className="w-4 h-4" />
                Download CSV
              </Button>
            </div>

            <pre className="max-h-64 max-w-full overflow-auto rounded-lg border bg-slate-50 p-3 text-xs leading-relaxed">
              {lines.slice(0, 8).join('\n')}
              {lines.length > 8 ? '\n…' : ''}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
